import {
  HttpClient,
  HttpErrorResponse,
  HttpParams
} from "@angular/common/http";
import {
  IServiceOptions,
  IServiceMethods,
  IServiceDataLoader
} from "./interfaces/utils.interface";
import {
  ResponseData,
  objectIsResponseModel,
  RequestSetting
} from "./helpers/response";
import {
  ModelState,
  DeletionMode,
  CrudAction,
  DataType,
  PushMode,
  EntityState,
  ROW_STATE_NAME,
  SaveMode
} from "./helpers/enums";
import { Injector, EventEmitter } from "@angular/core";
import { appInjector } from "./helpers/app-injector";
import { isFunction, isString } from "util";
import { CrudService } from "./components/crud/crud.service";
import { BlockerManager } from "./helpers/modal-option";
import { CacheDataContainer } from "./helpers/setting";
import { ResponseSetting } from "./helpers/response";
import { isValidType, getErrorMessage } from "./helpers/function";
import { Filter } from "./helpers/filtrable";
import { ServerMethods } from "./helpers/server-methods";
import { MessageService } from "primeng/api";
import { Observable } from "rxjs/internal/Observable";
import { Subscription } from "rxjs/internal/Subscription";
import { ConfigService } from "./services/config.service";
import { ComponentManager } from "./component-manager";

export class BaseObject {}

export class DataService {
  readonly GET = "GET";
  readonly POST = "POST";
  readonly PUT = "PUT";
  readonly DELETE = "DELETE";
  readonly PATCH = "PATCH";

  onError = new EventEmitter<string>();
  onRequest = new EventEmitter<string>();
  onResponse = new EventEmitter<string>();
  injector: Injector = appInjector();
  http: HttpClient;

  constructor() {
    this.http = this.injector.get(HttpClient);
  }

  httpGet(
    url: string,
    params?: any,
    callback?: Function
  ): Observable<ResponseData> {
    if (!url || url == "") {
      throw Error("You must specify a server method");
    }

    let options: any;

    if (arguments.length > 1) {
      if (isFunction(params)) {
        callback = params;
      } else {
        options = {
          params: new HttpParams({ fromObject: params })
        };
      }
    }

    let result: any = this.http.get<ResponseData>(url, options);

    if (callback) {
      this.onRequest.emit("get");

      result = result.subscribe(
        response => {
          this.onResponse.emit("get");
          if (!response.Commit) {
            this.onError.emit(response.Message);
            return;
          }
          callback(response);
          result.unsubscribe();
        },
        (error: HttpErrorResponse) => {
          this.onError.emit(getErrorMessage(error));
        }
      );
    }

    return result;
  }

  httpPost(
    url: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return this.httpVerb(this.POST, url, body, callback);
  }

  httpPut(
    url: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return this.httpVerb(this.PUT, url, body, callback);
  }

  httpPatch(
    url: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return this.httpVerb(this.PATCH, url, body, callback);
  }

  httpDelete(
    url: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return this.httpVerb(this.DELETE, url, body, callback);
  }

  httpVerb(
    verb: string,
    url: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    if (!url || url == "") {
      throw Error("You must specify a serve method");
    }

    let _body: any;

    if (arguments.length > 1) {
      if (isFunction(body)) {
        callback = body;
      } else {
        _body = body;
      }
    }

    let result: any;

    switch (verb) {
      case this.POST:
        result = this.http.post<ResponseData>(url, _body);
        break;

      case this.DELETE:
        result = this.http.delete<ResponseData>(url, _body);
        break;

      case this.PUT:
        result = this.http.put<ResponseData>(url, _body);
        break;

      case this.PATCH:
        result = this.http.patch<ResponseData>(url, _body);
        break;

      case this.GET:
        return this.httpGet(url, body, callback);

      default:
        throw Error("HTTP Verb doesn't supported");
    }

    if (callback) {
      this.onRequest.emit(verb);
      result = result.subscribe(
        response => {
          this.onResponse.emit(verb);
          if (!response.Commit) {
            this.onError.emit(response.Message);
            return;
          }

          if (isFunction(callback)) {
            callback(response);
          }
          result.unsubscribe();
        },
        (error: HttpErrorResponse) => {
          this.onError.emit(getErrorMessage(error));
        }
      );
    }

    return result;
  }
}

export class ServiceBase extends DataService {
  private _waiting = false;
  private _subscriptions: Subscription[] = [];
  protected canMakeRequestToServer: boolean;

  busy = false;

  configService: ConfigService;
  toastr: MessageService;
  crudService: CrudService;
  blocker: BlockerManager;

  get waiting(): boolean {
    return this._waiting;
  }

  set waiting(value: boolean) {
    if (this._waiting === value) {
      return;
    }

    this._waiting = value;

    if (this._waiting) {
      document.body.style.cursor = "wait";
    } else {
      document.body.style.cursor = "initial";
    }

    this.configService.blocker = this._waiting;
  }

  get urlResource(): string {
    return this.configService.getUrlApi(this.resourceUrl);
  }

  get url(): string {
    return this.configService.getUrlApi();
  }

  constructor(public resourceUrl?: string) {
    super();

    this.canMakeRequestToServer =
      this.resourceUrl && isString(this.resourceUrl);
    this.configService = this.injector.get(ConfigService);
    this.toastr = this.injector.get(MessageService);
    this.crudService = this.injector.get(CrudService);
    this.blocker = new BlockerManager(this.configService);
    this.subscribe();
  }

  getMethod(
    controllerMethod: string,
    params?: any,
    callback?: Function
  ): Observable<ResponseData> {
    return super.httpGet(
      `${this.urlResource}/${controllerMethod}`,
      params,
      callback
    );
  }

  postMethod(
    controllerMethod: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return super.httpPost(
      `${this.urlResource}/${controllerMethod}`,
      body,
      callback
    );
  }

  putMethod(
    controllerMethod: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return super.httpPut(
      `${this.urlResource}/${controllerMethod}`,
      body,
      callback
    );
  }

  patchMethod(
    controllerMethod: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return super.httpPatch(
      `${this.urlResource}/${controllerMethod}`,
      body,
      callback
    );
  }

  deleteMethod(
    controllerMethod: string,
    body?: any,
    callback?: any | Function
  ): Observable<ResponseData> {
    return super.httpDelete(
      `${this.urlResource}/${controllerMethod}`,
      body,
      callback
    );
  }

  Error(error: string) {
    Promise.resolve(null).then(() => {
      this.waiting = false;
      this.busy = false;
    });

    this.toastr.add({
      severity: "error",
      summary: "Error",
      detail: error
    });
  }

  Request() {
    Promise.resolve(null).then(() => {
      this.waiting = true;
    });
  }

  Response(method: string) {
    Promise.resolve(null).then(() => {
      this.waiting = false;

      switch (method) {
        case this.POST:
        case this.PUT:
        case this.DELETE:
        case this.PATCH:
          this.busy = false;
          break;
      }
    });
  }

  hasSubscriptions() {
    return this._subscriptions.length > 0;
  }

  subscribe() {
    this._subscriptions.push(
      this.onRequest.subscribe(() => this.Request()),
      this.onResponse.subscribe((method: string) => this.Response(method)),
      this.onError.subscribe((error: string) => this.Error(error))
    );
  }

  unsubscribe() {
    this._subscriptions.forEach(subscr => subscr.unsubscribe());
    this._subscriptions = [];
  }

  validateSubscriptions() {
    if (this._subscriptions.length == 0) {
      this.subscribe();
    }
  }
}

/*Servicio con funcionalidad cliente servidor*/
export class ServiceManager<T extends BaseObject> extends ServiceBase
  implements IServiceDataLoader<T> {
  private _state: ModelState = ModelState.Browse;
  private _defaultModelValue: T;
  private _prior: T;
  private _settingCanBeLoaded = true;
  private _saveMode: SaveMode = SaveMode.IDENTITY;

  readonly MODEL_STATE = ModelState;
  readonly CRUD_ACTION = CrudAction;
  loadEmitter = new EventEmitter();
  addEmitter = new EventEmitter();
  addedEmitter = new EventEmitter();
  editEmitter = new EventEmitter();
  editedEmitter = new EventEmitter();
  deleteEmitter = new EventEmitter();
  deletedEmitter = new EventEmitter();
  stateChangeEmitter = new EventEmitter();
  cancelEmitter = new EventEmitter();
  requeryCompleteEmitter = new EventEmitter();
  model: T;
  current: T;
  cacheData: any;
  identityPropertyName: string;
  resourceName: string;
  changeTracker: SingleChangeTracker;
  componentManager: ComponentManager<T>;
  data = new Array<T>();
  rowsPerPage = [10, 20, 50, 100, 200];
  saveAndNew = false;
  /**
   *Especifica el lugar desde donde es invocado el ServiceManager y se ejecuta en el siguiente orden:
    manager -> list: al hacer un refresh en el explorador desde el ListManager
    manager -> form: al hacer un refresh en el explorador desde el FormManager
   *
   * @type {('manager' | 'list' | 'form')}
   * @memberof ServiceManager
   */

  callerComponent = new CallerComponent();

  // = false;

  /*
    @saveAndKeep: boolean; para mantener el registro despues de ser guarado desde el modo de inserciÃ³n o ediciÃ³n.
    tiene mayor jerarquÃ­a que saveAndKeep: boolean.
  */
  saveAndKeep = false;
  requeryOnEdit = true;
  loadingOnAdd = false;
  loadingOnEdit = false;
  loadingOnDelete = false;
  loadingOnRequery = false;
  loadingOnGet = true;
  skipLoadingOnGetOnce = false;
  // skipSetStateOnce = false;
  //skipRequeryOnce = false;
  identityPropertyType: DataType = DataType.Number;
  deletionMode: DeletionMode = DeletionMode.Hard;
  pushMode: PushMode = PushMode.Last;
  requestSetting = new RequestSetting();
  responseSetting = new ResponseSetting();
  serverMethods = new ServerMethods();
  routingUrl: string;

  //TODO: puesto temporalmente
  Filter = new Filter();

  setOptions(options: IServiceOptions) {
    if (options.cacheDataProvider) {
      this.cacheData.providerOptions = options.cacheDataProvider;

      if (!this.cacheData.providerOptions.disableInitialization) {
        this.cacheData.init();
      }

      delete options.cacheDataProvider;
    }

    /*turn on/off filter in backend*/
    if (options.enableFilter) {
      this.requestSetting.EnableFilter = options.enableFilter;
      delete options.enableFilter;
    }

    if (options.loadCRUD) {
      this.requestSetting.LoadCRUD = options.loadCRUD;
      delete options.loadCRUD;
    }

    Object.assign(this, options);

    if (this.identityPropertyType != DataType.Number) {
      this._saveMode = SaveMode.WITHOUT_IDENTITY;
    }

    this.changeTracker.identityPropertyName = this.identityPropertyName;
  }

  get identityValue(): number | string {
    if (!this.identityPropertyName) {
      throw Error(
        `identityPropertyName ${this.identityPropertyName}, no definido`
      );
    }

    if (
      !isValidType(
        this.identityPropertyType,
        this.current[this.identityPropertyName]
      )
    ) {
      throw Error("El tipo de identity no es valido");
    }

    return this.current[this.identityPropertyName];
  }

  set identityValue(value: number | string) {
    this.current[this.identityPropertyName] = value;
  }

  get state(): ModelState {
    return this._state;
  }

  constructor(
    resourceUrl: string | typeof BaseObject,
    cls?: typeof BaseObject
  ) {
    super(<string>resourceUrl);

    this.callerComponent.state = "initializing";

    if (!isString(resourceUrl)) {
      cls = <typeof BaseObject>resourceUrl;
    }

    this._defaultModelValue = <T>new cls();
    this.setCurrent(this.getDefaultModel());
    this.requestSetting.Page.Size = this.rowsPerPage[0];
    this.cacheData = new CacheDataContainer(this);
    this.changeTracker = new SingleChangeTracker(this.identityPropertyName);
  }

  getDefaultModel(): T {
    const model = Object.assign({}, this._defaultModelValue);

    if (!this.identityPropertyName) {
      return model;
    }

    switch (this.identityPropertyType) {
      case DataType.Number:
        model[this.identityPropertyName] = 0;
        break;
      case DataType.String:
        model[this.identityPropertyName] = "";
        break;
      // case DataType.Boolean:
      //   model[this.identityPropertyName] = false;
      //   break;
      /*

         Modificado por: Alexis castro abreu 6/7/2019 5:11
         -------------------------------------------------------------------------------
         Un identity property solo puede soportar tipos de datos que se puedan
         utilizar como campos unicos en una base de datos o cualquier otro medio
         de almacenamiento un bool, date u otro cualquiera no deberia ser soportado.
         en el caso del bool solo permitiria dos registros. en otros faros es preferible
         convertir los formatos a string como en els caso de un date o guid.
     */
      default:
        throw new Error("No supported identityPropertyType");
    }

    return model;
  }

  getSubscription(
    requestSetting: RequestSetting,
    callback?: Function
  ): Observable<ResponseData> {
    return this.postMethod(this.serverMethods.GET, requestSetting, callback);
  }

  deleteSubscription(callback?: Function): Observable<ResponseData> {
    return this.postMethod(this.serverMethods.DELETE, this.model, callback);
  }

  addSubscription(callback?: Function): Observable<ResponseData> {
    return this.postMethod(this.serverMethods.ADD, this.model, callback);
  }

  editSubscription(callback?: Function): Observable<ResponseData> {
    return this.postMethod(this.serverMethods.EDIT, this.model, callback);
  }

  getSettingSubscription(
    requestSetting?: RequestSetting,
    callback?: Function
  ): Observable<ResponseData> {
    return this.postMethod(
      this.serverMethods.GET_SETTING,
      requestSetting,
      callback
    );
  }

  requerySubscription(
    identity: any,
    callback?: Function
  ): Observable<ResponseData> {
    return this.postMethod(
      this.serverMethods.REQUERY,
      { Value: identity },
      callback
    );
  }

  setModalFilter() {
    this.requestSetting.Filters = [];
    this.requestSetting.Filters.push(this.Filter);
  }

  load(requestSetting?: RequestSetting) {
    if (this.callerComponent.host != "form") {
      this.stateChange(ModelState.Loading);
    }

    requestSetting = Object.assign({}, this.requestSetting, requestSetting);

    if (this.cacheData && this.cacheData.providers.length > 0) {
      requestSetting.CacheDataProviders = this.cacheData.getCacheDataRequests();
    }

    this.configService.GlobalOptions.blocker.turnOffOnce = !this.loadingOnGet;

    if (this.skipLoadingOnGetOnce) {
      this.configService.GlobalOptions.blocker.turnOffOnce = true;
      this.skipLoadingOnGetOnce = !this.skipLoadingOnGetOnce;
    }

    this.getSubscription(requestSetting, (response: ResponseData) => {
      /**
			 YA QUE PUEDO ULTILIZAR EL SERVICIO SOLO PARA MANEJAR CRUD SIMPLE
			 ENTONCES VALIDO QUE SI NECESITO ALGUN OBJECT DE SETTING ESTE EXISTA
			 */
      this.data = response.DataSource;

      if (
        !response.Setting &&
        (this.requestSetting.EnableFilter || this.requestSetting.LoadCRUD)
      ) {
        throw Error(
          "Setting object is null and the current configuration needs an instance of it. If you do not want to use the Object setting, disable the RequestSetting.EnableFilter and RequestSetting.Load options"
        );
      }

      /*
			CARGA LA CONFIGURACION SOLO UNA VEZ.
			*/
      if (this._settingCanBeLoaded && response.Setting) {
        this._settingCanBeLoaded = false;

        //response.Setting.SecurityParam: SOLO TENDRA VALOR CUANDO SE ENVIA RequestSetting.LoadCRUD = true
        if (response.Setting.SecurityParam) {
          this.crudService.notifyCRUD(response.Setting.SecurityParam.CRUD);
        }

        //response.Setting.FilterStorage: SOLO TENDRA VALOR CUANDO SE ENVIA RequestSetting.EnableFilter = true
        if (response.Setting.FilterStorage) {
          //TODO:PENDIENTE DE IMPLEMENTACION
        }

        //response.Setting.CacheData: SOLO TENDRA VALOR SI SE RECIBE UN OBJECTO ResponseSetting.Setting.CacheData
        if (response.Setting.CacheData && this.cacheData) {
          //carga las listas para los select y otros
          this.cacheData.update(response.Setting.CacheData);
          //Para no mantener dos copias de CacheData en el cliente. Otra opciÃ³n serÃ­a encapsular Setting.CacheData en this.CacheData
          //pero no lo voy a complicar por ahora.
          delete response.Setting.CacheData;
        }

        Object.assign(this.responseSetting, response.Setting);
        this.requestSetting.Page.Size = this.rowsPerPage[0];
      }

      this.requestSetting.Page.RowCount = response.TotalItems;

      this.loadEmitter.emit({
        response: response,
        totalItems: this.requestSetting.Page.RowCount
      });

      if (this.callerComponent.host != "form") {
        this.stateChange(ModelState.Loaded, ModelState.Browse);
      }

      if (this.responseSetting.FilterStorage.length > 0) {
        this.Filter = response.Setting.FilterStorage[0];
      }
    });
  }

  add(model?: T) {
    if (!model) {
      model = this.getDefaultModel();
    }

    this.stateChange(ModelState.Add);

    this.setCurrent(model, args => {
      this.changeTracker.entry(args.model).state = EntityState.Detached;
      this.addEmitter.emit(args);
    });

    this.busy = false;
  }

  edit(model?: T) {
    if (!model) {
      model = this.model;
    }
    const oldState = this.state;

    this.stateChange(ModelState.Edit);

    this.setCurrent(model, args => {
      this.changeTracker.entry(args.model).attach(this._saveMode);
      if (
        this.componentManager &&
        (this.componentManager.isEditRouting ||
          this.callerComponent.host != "form")
      ) {
        this.editEmitter.emit(args);
      }
    });

    this.busy = false;
  }

  delete(model: T) {
    let canEmit = false;

    switch (this.deletionMode) {
      //Hard delete
      case DeletionMode.Hard:
        this.stateChange(ModelState.Delete);
        canEmit = true;
        break;

      case DeletionMode.Soft:
        canEmit = false;
        break;
    }

    this.setCurrent(model, args => {
      this.changeTracker.entry(args.model).markAsDeleted();

      if (canEmit) {
        this.deleteEmitter.emit(args);
      } else {
        this.updateCurrent(this.model);
        this.erase(args.model);
      }
    });

    this.busy = false;
  }

  erase(model: T) {
    let wasRemoved = false;

    if (
      this.deletionMode == DeletionMode.Hard ||
      (this.deletionMode == DeletionMode.Soft &&
        this.changeTracker.entry(model).canBeHardDeleted())
    ) {
      this.data.splice(this.data.indexOf(model), 1);
      this.requestSetting.Page.RowCount--;
      wasRemoved = true;
    }

    this.stateChange(
      wasRemoved ? ModelState.Deleted : ModelState.MaskAsDeleted,
      ModelState.Browse
    );
    this.deletedEmitter.emit({
      model: model,
      hardDeleted: wasRemoved
    });
  }

  localPost(model?: ResponseData | T) {
    if (model) {
      if (objectIsResponseModel(model)) {
        this.model = model.DataSource;
      } else {
        this.model = model;
      }
    }

    switch (this.state) {
      case ModelState.Add:
        this.changeTracker.entry(this.model).state = EntityState.Added;
        this.updateCurrent(this.model);
        this._push(this.model);
        this.stateChange(ModelState.Added, ModelState.Browse);
        this.requestSetting.Page.RowCount++;

        this.addedEmitter.emit({
          model: this.current,
          response: undefined
        });

        break;

      case ModelState.Edit:
        this.changeTracker.entry(this.model).saveChanges(this._saveMode);
        this.updateCurrent(this.model);
        this.stateChange(ModelState.Edited, ModelState.Browse);

        this.editedEmitter.emit({
          model: this.current,
          response: undefined
        });
        break;

      case ModelState.Delete:
        this.erase(this.current);
        break;

      case ModelState.Loading:
        break;
    }
  }

  post() {
    this.busy = true;

    switch (this.state) {
      case ModelState.Add:
        this.configService.GlobalOptions.blocker.turnOffOnce = !this
          .loadingOnAdd;

        switch (this._saveMode) {
          case SaveMode.WITHOUT_IDENTITY:
            this.changeTracker.entry(this.model).state = EntityState.Added;
            break;

          default:
            break;
        }

        this.addSubscription(response => {
          this.localPost(response.DataSource);
        });
        break;

      case ModelState.Edit:
        this.configService.GlobalOptions.blocker.turnOffOnce = !this
          .loadingOnEdit;

        switch (this._saveMode) {
          case SaveMode.WITHOUT_IDENTITY:
            this.changeTracker.entry(this.model).state = EntityState.Modified;
            break;

          default:
            break;
        }

        this.editSubscription(response => {
          this.localPost(response.DataSource);
        });
        break;

      case ModelState.Delete:
        this.configService.GlobalOptions.blocker.turnOffOnce = !this
          .loadingOnDelete;

        switch (this._saveMode) {
          case SaveMode.WITHOUT_IDENTITY:
            this.changeTracker.entry(this.model).state = EntityState.Deleted;
            break;

          default:
            break;
        }

        this.deleteSubscription(response => {
          this.localPost();
        });
        break;

      default:
        throw Error("Invalid post state");
    }
  }

  cancel() {
    this.current = this._prior;
    this.changeTracker.entry(this.model).reset();
    this.cancelEmitter.emit({ cancel: true });
    this.stateChange(ModelState.Canceled, ModelState.Browse);
  }

  //TODO:
  refresh(skipLoadingOnGetOnce = false) {
    this.skipLoadingOnGetOnce = skipLoadingOnGetOnce;
    if (!this.canMakeRequestToServer) {
      return;
    }

    this._settingCanBeLoaded = true;
    this.load();
  }

  setCurrent(currentModel: T, callback?: Function) {
    this._prior = this.current;
    this.current = currentModel;

    if (
      this.requeryOnEdit &&
      this._state == ModelState.Edit &&
      typeof this.resourceUrl === "string" &&
      this.identityPropertyName
      //!this.skipRequeryOnce
    ) {
      this.configService.GlobalOptions.blocker.turnOffOnce = !this
        .loadingOnRequery;

      this.requerySubscription(this.identityValue, response => {
        if (response.DataSource == null) {
          this.onError.emit("La consulta no arrojÃ³ ningun resultado");

          if (
            this.componentManager &&
            this.componentManager.routingUrl &&
            this.componentManager.routingUrl != ""
          ) {
            this.componentManager.router.navigate([
              this.componentManager.routingUrl
            ]);
          }

          return;
        }

        this.model = response.DataSource;
        this.requeryCompleteEmitter.emit(this.model);

        if (callback) {
          callback({
            model: this.model,
            response: undefined
          });
        }
      });
    } else {
      this.model = Object.assign({}, currentModel);

      if (callback) {
        callback({
          model: this.model,
          response: undefined
        });
      }
    }

    // if (this.skipRequeryOnce) {
    //   this.skipRequeryOnce = !this.skipRequeryOnce;
    // }
  }

  updateCurrent(currentModel: T) {
    Object.assign(this.current, currentModel);
  }

  stateChange(state: ModelState, curState?: ModelState) {
    if (state === this.state) {
      return;
    }

    // if (this.skipSetStateOnce) {
    //   this.skipSetStateOnce = !this.skipSetStateOnce;
    //   return;
    // }

    if (!curState) {
      curState = state;
    }

    this._state = curState;

    this.stateChangeEmitter.emit({
      state: state,
      current: curState,
      modified:
        state == ModelState.Added ||
        state == ModelState.Edited ||
        state == ModelState.MaskAsDeleted
    });
  }

  protected _push(current: T) {
    switch (this.pushMode) {
      case PushMode.First:
        this.data.splice(0, 0, current);

        if (this.data.length > this.requestSetting.Page.Size) {
          this.data.pop();
        }
        break;

      case PushMode.Current:
        throw Error("PushMode.Current NO IMPLEMENTADO");

      case PushMode.Last:
        if (this.data.length < this.requestSetting.Page.Size) {
          this.data.push(current);
        } else {
          this.data[this.data.length - 1] = current;
        }
        break;
    }

    this.requestSetting.Page.Size++;
  }

  trackByFn(index: number, model: T) {
    return model[this.identityPropertyName];
  }
}

export class ServiceSubscriber {
  private _suscriptions: Subscription[] = [];
  private _dataService: ServiceManager<any>;
  private _methodsHost: IServiceMethods;
  private _callback: Function;

  constructor(
    serviceManager: ServiceManager<any>,
    methodsHost?: IServiceMethods
  ) {
    this._dataService = serviceManager;
    this._methodsHost = methodsHost;

    this._dataService.validateSubscriptions();
  }

  callBack(callback: Function) {
    this._callback = callback;
  }

  private _callbackArgs(args: any, state: ModelState) {
    if (!this._callback) {
      return;
    }

    args["state"] = state;
    this._callback(args);
  }

  addSubscription(subscription: Subscription) {
    this._suscriptions.push(subscription);
  }

  subscribe() {
    this._suscriptions.push(
      this._dataService.addEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onAdd(args);
        } else {
          this._callbackArgs(args, ModelState.Add);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.addedEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onAdded(args);
        } else {
          this._callbackArgs(args, ModelState.Added);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.editEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onEdit(args);
        } else {
          this._callbackArgs(args, ModelState.Edit);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.editedEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onEdited(args);
        } else {
          this._callbackArgs(args, ModelState.Edited);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.deleteEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onDelete(args);
        } else {
          this._callbackArgs(args, ModelState.Delete);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.deletedEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onDeleted(args);
        } else {
          this._callbackArgs(args, ModelState.Deleted);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.cancelEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onCancel(args);
        } else {
          this._callbackArgs(args, ModelState.Canceled);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.loadEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onLoad(args);
        } else {
          this._callbackArgs(args, ModelState.Loaded);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.stateChangeEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onStateChange(args);
        } else {
          this._callbackArgs(args, ModelState.StateChange);
        }
      })
    );

    this._suscriptions.push(
      this._dataService.addEmitter.subscribe(args => {
        if (this._methodsHost) {
          this._methodsHost.onSettingRefreshed(args);
        } else {
          this._callbackArgs(args, ModelState.SettingChange);
        }
      })
    );
  }

  unsubscribe() {
    this._suscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });

    this._suscriptions = [];

    if (this._dataService.cacheData) {
      this._dataService.cacheData.destroy();
    }

    this._dataService.unsubscribe();
  }
}

export class SingleModelEntry {
  private _oldMark = `${ROW_STATE_NAME}__`;
  private _changeTracker: SingleChangeTracker;
  Model: any;

  constructor(model: any, changeTracker?: SingleChangeTracker) {
    this.Model = model;
    this._changeTracker = changeTracker;
  }

  get state(): EntityState {
    return this.Model[ROW_STATE_NAME];
  }

  set state(value: EntityState) {
    this.Model[this._oldMark] = this.state || value;
    this.Model[ROW_STATE_NAME] = value;
  }

  attach(mode?: SaveMode) {
    switch (mode) {
      case SaveMode.WITHOUT_IDENTITY:
        this.state = EntityState.Unchanged;
        break;

      case SaveMode.IDENTITY:
      default:
        this.state =
          this.identityValue == 0 ? EntityState.Added : EntityState.Unchanged;
        break;
    }
  }

  saveChanges(mode?: SaveMode) {
    switch (mode) {
      case SaveMode.WITHOUT_IDENTITY:
        this.state = EntityState.Modified;
        break;

      case SaveMode.IDENTITY:
      default:
        this.state =
          this.identityValue == 0 ? EntityState.Added : EntityState.Modified;
        break;
    }
  }

  reset() {
    this.Model[ROW_STATE_NAME] =
      this.Model[this._oldMark] || EntityState.Detached;
  }

  canBeHardDeleted() {
    return this.identityValue == 0 && this.state == EntityState.Deleted;
  }

  markAsModified() {
    this.state = EntityState.Modified;
  }

  markAsDeleted() {
    this.state = EntityState.Deleted;
  }

  markAsAdded() {
    this.state = EntityState.Added;
  }

  hasChanges(): boolean {
    return (
      this.state == EntityState.Added ||
      this.state == EntityState.Modified ||
      this.state == EntityState.Deleted
    );
  }

  touchable(): boolean {
    return (
      this.state == EntityState.Added ||
      this.state == EntityState.Modified ||
      this.state == EntityState.Unchanged
    );
  }

  get identityValue(): number {
    const identity = parseInt(
      this.Model[this._changeTracker.identityPropertyName]
    );

    return isNaN(identity) ? 0 : identity;
  }
}

export class SingleChangeTracker {
  constructor(public identityPropertyName: string) {}

  entry(model: any): SingleModelEntry {
    return new SingleModelEntry(model, this);
  }
}

export class CallerComponent {
  host: "manager" | "list" | "form";
  state: "initializing" | "destroying";

  resetState() {
    this.state = undefined;
  }
}
